/**
 * Construction schedule date helpers.
 * By default, "days to complete" counts weekdays only (Mon–Fri).
 */

export function parseScheduleDateOnly(value) {
  if (value == null || String(value).trim() === "") return null;
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatScheduleDateOnly(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function formatScheduleDateLabel(value) {
  const d = parseScheduleDateOnly(value);
  if (!d) return value != null ? String(value).trim() : "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function isWeekendDate(date) {
  const day = date.getDay();
  return day === 0 || day === 6;
}

/** Next Mon–Fri on or after the given date. */
export function firstWorkdayOnOrAfter(date) {
  const cursor = new Date(date);
  while (isWeekendDate(cursor)) {
    cursor.setDate(cursor.getDate() + 1);
  }
  return cursor;
}

/** Inclusive calendar days between two dates (e.g. Mon–Sun = 7). */
export function inclusiveCalendarDaysBetween(startStr, endStr) {
  const start = parseScheduleDateOnly(startStr);
  const end = parseScheduleDateOnly(endStr);
  if (!start || !end || end < start) return null;
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((end.getTime() - start.getTime()) / msPerDay) + 1;
}

/** Inclusive weekday count between two dates (Mon–Fri only). */
export function workdaysBetween(startStr, endStr) {
  const start = parseScheduleDateOnly(startStr);
  const end = parseScheduleDateOnly(endStr);
  if (!start || !end || end < start) return null;
  let count = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    if (!isWeekendDate(cursor)) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

export function durationDaysBetween(startStr, endStr, includeWeekends = false) {
  return includeWeekends
    ? inclusiveCalendarDaysBetween(startStr, endStr)
    : workdaysBetween(startStr, endStr);
}

export function readConstructionDaysValue(value) {
  if (value == null || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) return "";
  return Math.round(n);
}

export function readIncludeWeekends(value) {
  return value === true || value === "true" || value === 1 || value === "1";
}

/**
 * Planned end from start + day count.
 * Weekdays only unless includeWeekends; weekend starts roll forward to the next weekday for counting.
 */
export function plannedEndFromStartAndDays(startStr, days, includeWeekends = false) {
  const start = parseScheduleDateOnly(startStr);
  const dayCount = readConstructionDaysValue(days);
  if (!start || dayCount === "") return "";

  if (includeWeekends) {
    const end = new Date(start);
    end.setDate(end.getDate() + dayCount - 1);
    return formatScheduleDateOnly(end);
  }

  let cursor = firstWorkdayOnOrAfter(start);
  let remaining = dayCount - 1;
  while (remaining > 0) {
    cursor.setDate(cursor.getDate() + 1);
    if (!isWeekendDate(cursor)) remaining -= 1;
  }
  return formatScheduleDateOnly(cursor);
}

export function daysFromStartAndEnd(startStr, endStr, includeWeekends = false) {
  return durationDaysBetween(startStr, endStr, includeWeekends);
}

export function durationDayLabel(includeWeekends) {
  return includeWeekends ? "day" : "workday";
}

export function durationDaysLabel(count, includeWeekends) {
  const unit = durationDayLabel(includeWeekends);
  return `${count} ${unit}${count === 1 ? "" : "s"}`;
}

/** @deprecated Use durationDaysBetween or workdaysBetween */
export function inclusiveDaysBetween(startStr, endStr) {
  return inclusiveCalendarDaysBetween(startStr, endStr);
}
